import cache from '../../../cache/cache.js';
import ImageQualityStatus from '../../../enums/ImageQualityStatus.js';
import ViewportType from '../../../enums/ViewportType.js';
import { compareVoxelQuality, voxelGridsEqual, } from '../../../utilities/voxelGrid/index.js';
import { sampleDistanceRatiosOf } from '../createVolumeMapper.js';
import { reductionCauseOf } from '../volumeRenderStrategy.js';
import { isVolumeLoading } from '../../GenericViewport/renderingFidelity.js';
import { gpuSettingsForRender } from '../../../utilities/gpuSettingsPresets.js';
import { displaySpacingForGrid, fidelityDisplayOfCamera, } from './displaySpacingOfViewport.js';
import { isLabelmapActorEntry } from './isLabelmapActorEntry.js';
import { isReduced, reductionFactorsOf, } from './fidelityIndicatorState.js';
export function fidelityLinesFromBindings(display, sources) {
    const lines = [];
    for (const source of sources) {
        if (source.skip) {
            continue;
        }
        if (!source.record) {
            if (source.imageQualityStatus !== undefined) {
                lines.push({
                    ...fidelityLineFromImageQuality(source.imageQualityStatus, source.qualityPending),
                    label: source.label ?? 'Image',
                    interactiveReduction: source.interactiveReduction,
                });
            }
            continue;
        }
        const { record } = source;
        const fullGrid = source.fullGrid ?? record.grid;
        const reductionFactors = reductionFactorsOf(record.grid, fullGrid);
        const fullResolution = !isReduced(reductionFactors);
        const displaySpacing = display
            ? displaySpacingForGrid(display, record.grid, fullGrid)
            : undefined;
        const verdict = displaySpacing
            ? compareVoxelQuality(record, { displaySpacing })
            : undefined;
        lines.push({
            label: source.label ?? 'Image',
            record,
            verdict,
            displaySpacing,
            reductionFactors,
            fullResolution,
            sampleDistanceLod: source.sampleDistanceLod ?? 1,
            sampleDistanceIdle: source.sampleDistanceIdle,
            interactiveReduction: source.interactiveReduction,
            qualityPending: source.qualityPending,
            reductionCause: source.reductionCause,
        });
    }
    return lines;
}
export function fidelityLinesFromVolumeActors(viewport, records = new Map()) {
    const actors = viewport.getActors();
    if (!actors.length) {
        return [];
    }
    const volume3D = isVolume3D(viewport);
    const display = fidelityDisplayOfCamera(viewport, volume3D);
    const lines = [];
    const seen = new Set();
    for (const entry of actors) {
        const id = entry.referencedId ?? entry.uid;
        const volume = id ? cache.getVolume(id) : undefined;
        if (!volume || seen.has(id) || isLabelmapActorEntry(entry)) {
            continue;
        }
        seen.add(id);
        const mapper = entry.actor.getMapper?.();
        const texture = mapper?.getScalarTexture?.();
        const grid = texture?.getGrid?.() ?? volume.voxelGrid;
        const fullGrid = volume.voxelGrid;
        let cached = records.get(id);
        if (!cached || !voxelGridsEqual(cached.grid, grid)) {
            const statistic = texture?.getStatistic?.() ?? volume.reductionStatistic;
            cached = {
                grid,
                record: volume.getGridQuality(grid, statistic),
            };
            records.set(id, cached);
        }
        const reductionFactors = reductionFactorsOf(grid, fullGrid);
        const fullResolution = !isReduced(reductionFactors);
        const record = cached.record;
        const sampleDistance = volume3D
            ? sampleDistanceRatiosOf(mapper)
            : { current: 1, idle: 1 };
        const displaySpacing = display
            ? displaySpacingForGrid(display, grid, fullGrid)
            : undefined;
        const verdict = record && displaySpacing
            ? compareVoxelQuality(record, { displaySpacing })
            : undefined;
        lines.push({
            label: 'Image',
            record: record ?? undefined,
            verdict,
            displaySpacing,
            reductionFactors,
            fullResolution,
            sampleDistanceLod: sampleDistance.current,
            sampleDistanceIdle: sampleDistance.idle,
            qualityPending: isVolumeLoading(volume),
            reductionCause: fullResolution
                ? undefined
                : reductionCauseOf(volume, gpuSettingsForRender()),
        });
    }
    return lines;
}
function isVolume3D(viewport) {
    return (viewport.type === ViewportType.VOLUME_3D ||
        viewport.type === ViewportType.PERSPECTIVE);
}
function fidelityLineFromImageQuality(imageQualityStatus, qualityPending) {
    return {
        label: 'Image',
        imageQualityStatus,
        qualityPending,
        reductionFactors: [1, 1, 1],
        fullResolution: imageQualityStatus === ImageQualityStatus.FULL_RESOLUTION,
        sampleDistanceLod: 1,
    };
}
export function fidelityLineFromStackImage(image) {
    if (!image) {
        return [];
    }
    return [
        fidelityLineFromImageQuality(imageQualityStatusOf(image), image.imageQualityPending),
    ];
}
export function imageQualityStatusOf(image) {
    return image
        ? (image.imageQualityStatus ?? ImageQualityStatus.FULL_RESOLUTION)
        : undefined;
}
